/**
 * Runtime API-origin derivation — the client-side mirror of
 * `functions/_proxy.ts` `resolveUpstream` (#233 clean-A, #234).
 *
 * One code path decides the API origin for EVERY environment, so no
 * per-env build config can drift:
 *
 *   golink.papan.ai              → https://golink-api.papan.ai
 *   stagetest.papan.ai           → https://stagetest-api.papan.ai
 *   os.papan.ai                 → https://api.papan.ai
 *   <slug>.papan-4dj.pages.dev   → https://pr-<slug>-api.papan.ai   (PR review envs)
 *   anything else                → '' (same-origin: localhost dev via the
 *                                  vite proxy, unknown hosts fall back to
 *                                  the Pages-Function proxy)
 *
 * `VITE_API_URL` / `VITE_WS_URL`, when set at build time, still win over
 * derivation (see index.ts) — that is the explicit override / rollback
 * lever, not the normal operating mode. The end state (#235) is: no
 * VITE_API_URL anywhere, derivation drives all envs identically.
 *
 * Keep the map + PR pattern in sync with functions/_proxy.ts until that
 * file is deleted (#235).
 */

/**
 * Per-PR review env: <branch-slug>.papan-4dj.pages.dev → pr-<slug>-api.papan.ai.
 * CF Pages publishes two hostname shapes for preview deployments: the branch
 * alias (`feat-foo`) and the commit hash (`a1b2c3d4`). Only the branch alias
 * has a per-PR backend, so require a hyphen in the label to skip the hash
 * form (mirrors usePreviewSession's PR_PREVIEW_HOSTNAME) — hash-form URLs
 * fall back to the baked VITE_PREVIEW_ORIGIN, else same-origin (where /s/ is not served
 * on a deployment that has a preview host, #150).
 */
const PR_PREVIEW_HOSTNAME = /^([a-z0-9]+(?:-[a-z0-9]+)+)\.papan-4dj\.pages\.dev$/i

/** Stable hostnames with a dedicated -api origin. Whitelabel installs
 * (custom zones) get explicit entries too — their CF Pages projects
 * deliberately do NOT set VITE_API_URL (clean-A runtime derivation, #235),
 * so a missing entry here silently falls back to same-origin and breaks
 * every API call on a split-origin install. */
const STABLE_HOST_TO_API: Record<string, string> = {
  'golink.papan.ai': 'golink-api.papan.ai',
  'stagetest.papan.ai': 'stagetest-api.papan.ai',
  'stagetest2.papan.ai': 'stagetest2-api.papan.ai',
  'os.papan.ai': 'api.papan.ai',
  'app.gnexis.ai': 'api.gnexis.ai',
  'os.gnexis.ai': 'api.gnexis.ai', // gnexis's new frontend host (app.gnexis.ai kept while it redirects)
}

/**
 * Stable hostnames → their sandboxed preview-serve origin (#235). The preview
 * origin hosts /preview/* and /s/* user content on a dedicated subdomain —
 * cross-origin to both the SPA and the API host, so iframe sub-requests can't
 * ride user auth cookies. PR envs use preview-pr-<slug>-api.papan.ai
 * (provisioned by deploy-pr.sh).
 */
const STABLE_HOST_TO_PREVIEW: Record<string, string> = {
  'golink.papan.ai': 'preview-golink.papan.ai',
  'stagetest.papan.ai': 'preview-stagetest.papan.ai',
  'stagetest2.papan.ai': 'preview-stagetest2.papan.ai',
  'os.papan.ai': 'preview.papan.ai',
  'app.gnexis.ai': 'preview.gnexis.ai',
  'os.gnexis.ai': 'preview.gnexis.ai',
}

/**
 * Derive the API origin (https://…, no trailing slash) for a frontend
 * hostname, or '' when the host has no dedicated -api origin (same-origin
 * fallback).
 */
export function deriveApiOrigin(hostname: string): string {
  const pr = hostname.match(PR_PREVIEW_HOSTNAME)
  if (pr) return `https://pr-${pr[1].toLowerCase()}-api.papan.ai`
  const stable = STABLE_HOST_TO_API[hostname.toLowerCase()]
  return stable ? `https://${stable}` : ''
}

/**
 * WS variant of deriveApiOrigin: wss://<api-host>, or '' for same-origin.
 * A truthy result routes the realtime client onto the ws-token path
 * (features/realtime/client/useRealtime.tsx) — token-in-query auth, no
 * cookie dependency on the upgrade.
 */
export function deriveWsOrigin(hostname: string): string {
  const api = deriveApiOrigin(hostname)
  return api ? api.replace(/^https:/, 'wss:') : ''
}

/**
 * Derive the sandboxed preview-serve origin (https://…) for a frontend
 * hostname, or '' when unknown (same-origin fallback — the dev vite proxy; a deployment
 * with a preview host serves /s/ nowhere else, #150). Serves /s/* user content.
 */
export function derivePreviewOrigin(hostname: string): string {
  const pr = hostname.match(PR_PREVIEW_HOSTNAME)
  if (pr) return `https://preview-pr-${pr[1].toLowerCase()}-api.papan.ai`
  const stable = STABLE_HOST_TO_PREVIEW[hostname.toLowerCase()]
  return stable ? `https://${stable}` : ''
}
